import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { Sparkles, CheckCircle2, Users, Shield, Zap } from 'lucide-react'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { useAuthStore, Badge } from '@personic/shared'

const features = [
  {
    icon: Users,
    title: 'Set up your profile',
    description: 'Personalize your account with your details',
  },
  {
    icon: Shield,
    title: 'Configure your organization',
    description: 'Add your company information and preferences',
  },
  {
    icon: Zap,
    title: 'Choose your products',
    description: 'Select the Personic AI products for your team',
  },
  {
    icon: CheckCircle2,
    title: 'Invite your team',
    description: 'Add team members to collaborate together',
  },
]

export default function Welcome() {
  const { user } = useAuthStore()
  const { accountType, setStep } = useOnboardingStore()

  // Ensure we're on step 1
  useEffect(() => {
    setStep(1)
  }, [setStep])

  const firstName = user?.name?.split(' ')[0] || 'there'

  return (
    <div className="text-center">
      {/* Animated Illustration */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
        className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-copper-400 to-copper-600 rounded-3xl flex items-center justify-center shadow-xl"
      >
        <Sparkles className="w-12 h-12 text-white" />
      </motion.div>

      {/* Heading */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <h1 className="text-3xl md:text-4xl font-heading font-bold text-midnight-900 mb-3">
          Welcome to Personic AI, {firstName}!
        </h1>

        {/* Account Type Badge */}
        {accountType && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="inline-flex"
          >
            <Badge
              variant={accountType === 'business' ? 'copper' : 'midnight'}
              className="text-sm px-4 py-1"
            >
              {accountType === 'business' ? 'Business Account' : 'Developer Account'}
            </Badge>
          </motion.div>
        )}

        <p className="text-lg text-warm-600 mt-4 max-w-xl mx-auto">
          Let's get your account set up in just a few minutes. We'll walk you through
          everything you need to start using Personic AI.
        </p>
      </motion.div>

      {/* Feature List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 text-left"
      >
        {features.map((feature, index) => {
          const Icon = feature.icon
          return (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + index * 0.1 }}
              className="flex items-start gap-4 p-4 rounded-xl bg-midnight-50/50"
            >
              <div className="w-10 h-10 rounded-lg bg-copper-100 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-copper-600" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-midnight-900">
                  {feature.title}
                </h3>
                <p className="text-sm text-warm-500">{feature.description}</p>
              </div>
            </motion.div>
          )
        })}
      </motion.div>

      {/* Encouragement */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
        className="mt-8 text-sm text-warm-500"
      >
        This should only take about 2-3 minutes
      </motion.p>
    </div>
  )
}
